import { RESOURCES, ACTIONS } from '@/constants/permissions'
import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

// Auth Default Routes
const authChildRoutes = (prefix) => [
  {
    path: 'login',
    name: prefix + '.login',
    meta: { auth: false, name: 'Login' },
    component: () => import('@/views/auth/default/SignIn.vue')
  },
  {
    path: 'reset-password',
    name: prefix + '.reset-password',
    meta: { auth: false, name: 'Reset Password' },
    component: () => import('@/views/auth/default/ResetPassword.vue')
  },
  {
    path: 'change-password',
    name: prefix + '.change-password',
    meta: { auth: false, name: 'Change Password', requiresPasswordChangeToken: true },
    component: () => import('@/views/auth/default/ChangeExpiredPassword.vue')
  }
]

// Default routes
const defaultChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, name: 'Home', isBanner: false, permission: { resource: RESOURCES.DASHBOARD, action: ACTIONS.READ } },
    component: () => import('@/views/dashboards/IndexPage.vue')
  },
  {
    path: 'no-dashboard',
    name: prefix + '.empty',
    meta: { auth: true, name: 'Home', isBanner: false },
    component: () => import('@/views/EmptyPage.vue')
  },
  {
    path: 'charging-dashboard',
    name: prefix + '.charging-dashboard',
    meta: { auth: true, name: 'Charging Dashboard', isBanner: false, permission: { resource: RESOURCES.CHARGING_DASHBOARD, action: ACTIONS.READ } },
    component: () => import('@/views/dashboards/ChargingPage.vue')
  },
  // Promotions Pages
  {
    path: '/promotion-list',
    name: prefix + '.promotion-list',
    meta: { auth: true, name: 'Promotion List', isBanner: true, permission: { resource: RESOURCES.PROMOTION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/promotion/ListPage.vue')
  },
  {
    path: '/promotion-add',
    name: prefix + '.promotion-add',
    meta: { auth: true, name: 'Promotion Add', isBanner: true, permission: { resource: RESOURCES.PROMOTION_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/promotion/AddPage.vue')
  },
  {
    path: '/promotion-edit/:id',
    name: prefix + '.promotion-edit',
    meta: { auth: true, name: 'Promotion Edit', isBanner: true, permission: { resource: RESOURCES.PROMOTION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/promotion/EditPage.vue')
  },
  {
    path: '/promotion-view/:id',
    name: prefix + '.promotion-view',
    meta: { auth: true, name: 'Promotion View', isBanner: true, permission: { resource: RESOURCES.PROMOTION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/promotion/ViewPage.vue')
  },
  // Voucher Pages
  {
    path: '/voucher-list',
    name: prefix + '.voucher-list',
    meta: { auth: true, name: 'Voucher Management', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/voucher/ListPage.vue')
  },
  {
    path: '/voucher-add',
    name: prefix + '.voucher-add',
    meta: { auth: true, name: 'Add Voucher', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/voucher/AddPage.vue')
  },
  // Company Pages
  {
    path: '/company-list',
    name: prefix + '.company-list',
    meta: { auth: true, name: 'Company List', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/company/ListPage.vue')
  },
  {
    path: '/company-add',
    name: prefix + '.company-add',
    meta: { auth: true, name: 'Company Add', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/company/AddPage.vue')
  },
  {
    path: '/company-edit/:id',
    name: prefix + '.company-edit',
    meta: { auth: true, name: 'Company Edit', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/company/EditPage.vue')
  },
  // Resource Pages (admin only)
  {
    path: '/resource-list',
    name: prefix + '.resource-list',
    meta: { auth: true, name: 'Resource List', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/resource/ListPage.vue')
  },
  {
    path: '/resource-add',
    name: prefix + '.resource-add',
    meta: { auth: true, name: 'Resource Add', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/resource/AddPage.vue')
  },
  {
    path: '/resource-edit/:id',
    name: prefix + '.resource-edit',
    meta: { auth: true, name: 'Resource Edit', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/resource/EditPage.vue')
  },
  // Permission Pages (admin only)
  {
    path: '/permission-groups',
    name: prefix + '.permission-groups',
    meta: { auth: true, name: 'Permission Groups', isBanner: false, permission: { resource: RESOURCES.PERMISSION_GROUPS, action: ACTIONS.READ } },
    component: () => import('@/views/permission/PermissionGroupPage.vue')
  },
  {
    path: '/permission-group-add',
    name: prefix + '.permission-group-add',
    meta: { auth: true, name: 'Permission Group Add', isBanner: true, permission: { resource: RESOURCES.PERMISSION_GROUPS, action: ACTIONS.READ } },
    component: () => import('@/views/permission/AddGroupPage.vue')
  },
  // Plug Interface Pages
  {
    path: '/plug-interface-list',
    name: prefix + '.plug-interface-list',
    meta: { auth: true, name: 'Plug Interface List', isBanner: true },
    component: () => import('@/views/plug-interface/ListPage.vue')
  },
  {
    path: '/plug-interface-add',
    name: prefix + '.plug-interface-add',
    meta: { auth: true, name: 'Plug Interface Add', isBanner: true },
    component: () => import('@/views/plug-interface/AddPage.vue')
  },
  {
    path: '/plug-interface-edit/:id',
    name: prefix + '.plug-interface-edit',
    meta: { auth: true, name: 'Plug Interface Edit', isBanner: true },
    component: () => import('@/views/plug-interface/EditPage.vue')
  },
  // Charging Station Pages
  {
    path: '/charging-station-list',
    name: prefix + '.charging-station-list',
    meta: { auth: true, name: 'Charging Station List', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/ListPage.vue')
  },
  {
    path: '/charging-stations/tags',
    alias: '/charging-station-tags',
    name: prefix + '.charging-station-tags',
    meta: { auth: true, name: 'Charging Station Tags', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_TAGS, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/TagsPage.vue')
  },
  {
    path: '/charging-station-add',
    name: prefix + '.charging-station-add',
    meta: { auth: true, name: 'Charging Station Add', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/AddPage.vue')
  },
  {
    path: '/charging-station-edit/:id',
    name: prefix + '.charging-station-edit',
    meta: { auth: true, name: 'Charging Station Edit', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/EditPage.vue')
  },
  {
    path: '/charging-station-balance',
    name: prefix + '.charging-station-balance',
    meta: { auth: true, name: 'Charging Stations Balance', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_BALANCE, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/BalancePage.vue')
  },
  {
    path: '/charging-station-balance-tracking',
    name: prefix + '.charging-station-balance-tracking',
    meta: { auth: true, name: 'Charging Station Balance Tracking', isBanner: true, permission: { resource: RESOURCES.CHARGING_STATION_TRANSACTIONS, action: ACTIONS.READ } },
    component: () => import('@/views/charging-station/BalanceTrackingPage.vue')
  },
  // Charger Pages
  {
    path: '/charger-list',
    name: prefix + '.charger-list',
    meta: { auth: true, name: 'Charger List', isBanner: true, permission: { resource: RESOURCES.CHARGER_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/charger/ListPage.vue')
  },
  {
    path: '/charger-add',
    name: prefix + '.charger-add',
    meta: { auth: true, name: 'Charger Add', isBanner: true, permission: { resource: RESOURCES.CHARGER_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/charger/AddPage.vue')
  },
  {
    path: '/charger-edit/:id',
    name: prefix + '.charger-edit',
    meta: { auth: true, name: 'Charger Edit', isBanner: true, permission: { resource: RESOURCES.CHARGER_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/charger/EditPage.vue')
  },
  {
    path: '/charger-dashboard',
    name: prefix + '.charger-dashboard',
    meta: { auth: true, name: 'Charger Dashboard', isBanner: false, permission: { resource: RESOURCES.CHARGER_DASHBOARD, action: ACTIONS.READ } },
    component: () => import('@/views/charger/DashboardPage.vue')
  },
  {
    path: '/load-balancing',
    name: prefix + '.load-balancing',
    meta: { auth: true, name: 'Load Balancing', isBanner: true, permission: { resource: RESOURCES.LOAD_BALANCING, action: ACTIONS.READ } },
    component: () => import('@/views/charger/LoadBalancing.vue')
  },
  {
    path: '/ev-operators-list',
    name: prefix + '.ev-operators-list',
    meta: { auth: true, name: 'EV Operators List', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/ListPage.vue')
  },
  {
    path: '/ev-operator-add',
    name: prefix + '.ev-operator-add',
    meta: { auth: true, name: 'EV Operator Add', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/AddPage.vue')
  },
  {
    path: '/ev-operator-edit/:id',
    name: prefix + '.ev-operator-edit',
    meta: { auth: true, name: 'EV Operators Edit', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/EditPage.vue')
  },
  {
    path: '/ev-operator-mapping',
    name: prefix + '.ev-operator-mapping',
    meta: { auth: true, name: 'EV Operators Charger Mapping', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_MAPPING, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/MappingPage.vue')
  },
  {
    path: '/ev-operator-balance',
    name: prefix + '.ev-operator-balance',
    meta: { auth: true, name: 'EV Operators Balance', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_BALANCE, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/BalancePage.vue')
  },
  {
    path: '/ev-operator-balance-tracking',
    name: prefix + '.ev-operator-balance-tracking',
    meta: { auth: true, name: 'EV Operators Balance Tracking', isBanner: true, permission: { resource: RESOURCES.EV_OPERATOR_TRANSACTIONS, action: ACTIONS.READ } },
    component: () => import('@/views/ev-operators/BalanceTrackingPage.vue')
  },
  {
    path: '/network-list',
    name: prefix + '.network-list',
    meta: { auth: true, name: 'Network List', isBanner: true, permission: { resource: RESOURCES.NETWORK_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/network/ListPage.vue')
  },
  {
    path: '/network-add',
    name: prefix + '.network-add',
    meta: { auth: true, name: 'Network Add', isBanner: true, permission: { resource: RESOURCES.NETWORK_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/network/AddPage.vue')
  },
  {
    path: '/network-edit/:id',
    name: prefix + '.network-edit',
    meta: { auth: true, name: 'Network Edit', isBanner: true, permission: { resource: RESOURCES.NETWORK_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/network/EditPage.vue')
  },
  {
    path: '/user-list',
    name: prefix + '.user-list',
    meta: { auth: true, name: 'User List', isBanner: true, permission: { resource: RESOURCES.USER_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/user/ListPage.vue')
  },
  {
    path: '/user-add',
    name: prefix + '.user-add',
    meta: { auth: true, name: 'User Add', isBanner: true, permission: { resource: RESOURCES.USER_ADD, action: ACTIONS.READ } },
    component: () => import('@/views/user/AddPage.vue')
  },
  {
    path: '/user-edit/:id',
    name: prefix + '.user-edit',
    meta: { auth: true, name: 'User Edit', isBanner: true, permission: { resource: RESOURCES.USER_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/user/EditPage.vue')
  },
  {
    path: '/user-profile',
    name: prefix + '.user-profile',
    meta: { auth: true, name: 'User Profile', isBanner: true },
    component: () => import('@/views/user/ProfilePage.vue')
  },
  {
    path: '/privacy-setting',
    name: prefix + '.user-privacy-setting',
    meta: { auth: true, name: 'Privacy Setting', isBanner: true },
    component: () => import('@/views/user/PrivacySetting.vue')
  },
  {
    path: '/privacy-policy',
    name: prefix + '.privacy-policy',
    meta: { auth: true, name: 'Privacy Policy', isBanner: true },
    component: () => import('@/views/extra/PrivacyPolicy.vue')
  },
  {
    path: '/terms-and-conditions',
    name: prefix + '.terms-and-conditions',
    meta: { auth: true, name: 'Terms and Conditions', isBanner: true },
    component: () => import('@/views/extra/TermsAndConditions.vue')
  },
  {
    path: '/admin-permissions',
    name: prefix + '.admin-permissions',
    meta: { auth: true, name: 'Admin Permissions', isBanner: true },
    component: () => import('@/views/admin/AdminPage.vue')
  },
  {
    path: '/admin/platform-config',
    name: prefix + '.admin.platform-config.list',
    meta: { auth: true, name: 'Platform Configuration', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/admin/platform-config/ListPage.vue')
  },
  {
    path: '/admin/platform-config/add',
    name: prefix + '.admin.platform-config.add',
    meta: { auth: true, name: 'Add Configuration', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/admin/platform-config/EditPage.vue')
  },
  {
    path: '/admin/platform-config/edit/:key',
    name: prefix + '.admin.platform-config.edit',
    meta: { auth: true, name: 'Edit Configuration', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/admin/platform-config/EditPage.vue')
  },
  {
    path: '/session-list',
    name: prefix + '.session-list',
    meta: { auth: true, name: 'Session List', isBanner: true, permission: { resource: RESOURCES.SESSION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/session/ListPage.vue')
  },
  {
    path: '/session-anomalies',
    name: prefix + '.session-anomalies',
    meta: { auth: true, name: 'Session Anomalies', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.session-list' })
      }
    },
    component: () => import('@/views/session/AnomaliesPage.vue')
  },
  {
    path: '/session-detail/:id',
    name: prefix + '.session-detail',
    meta: { auth: true, name: 'Session Detail', isBanner: true, permission: { resource: RESOURCES.SESSION_LIST, action: ACTIONS.READ } },
    component: () => import('@/views/session/DetailPage.vue')
  },
  {
    path: '/shift-list',
    name: prefix + '.shift-list',
    meta: { auth: true, name: 'Shift List', isBanner: true, permission: { resource: RESOURCES.PREVIOUS_SHIFTS, action: ACTIONS.READ } },
    component: () => import('@/views/shift/ListPage.vue')
  },
  {
    path: '/active-shift',
    name: prefix + '.active-shift',
    meta: { auth: true, name: 'Active Shift', isBanner: true, permission: { resource: RESOURCES.ACTIVE_SHIFTS, action: ACTIONS.READ } },
    component: () => import('@/views/shift/ActiveShiftPage.vue')
  },
  {
    path: '/previous-shift/:id',
    name: prefix + '.previous-shift',
    meta: { auth: true, name: 'Previous Shift', isBanner: true, permission: { resource: RESOURCES.PREVIOUS_SHIFTS, action: ACTIONS.READ } },
    component: () => import('@/views/shift/PreviousShiftPage.vue')
  },
  {
    path: '/session-summary',
    name: prefix + '.session-summary',
    meta: { auth: true, name: 'Session Summary', isBanner: true, permission: { resource: RESOURCES.SESSION_SUMMARY, action: ACTIONS.READ } },
    component: () => import('@/views/session/SummaryPage.vue')
  },
  {
    path: '/status-notifications',
    name: prefix + '.status-notifications',
    meta: { auth: true, name: 'Status Notifications', isBanner: true, permission: { resource: RESOURCES.STATUS_NOTIFICATIONS, action: ACTIONS.READ } },
    component: () => import('@/views/status-notifications/StatusNotification.vue')
  },
  {
    path: '/diagnostics',
    name: prefix + '.monitoring-diagnostics',
    meta: { auth: true, name: 'Diagnostics Requests', isBanner: true, permission: { resource: 'diagnostics_logs', action: 'read' } },
    component: () => import('@/views/diagnostics/DiagnosticsRequestsPage.vue')
  },
  {
    path: '/diagnostics/create',
    name: prefix + '.monitoring-diagnostics-create',
    meta: { auth: true, name: 'Create Diagnostics Request', isBanner: true, permission: { resource: 'diagnostics_logs', action: 'create' } },
    component: () => import('@/views/diagnostics/CreateDiagnosticsRequestPage.vue')
  },
  {
    path: '/diagnostics/:requestId',
    name: prefix + '.monitoring-diagnostics-details',
    meta: { auth: true, name: 'Diagnostics Request Details', isBanner: true, permission: { resource: 'diagnostics_logs', action: 'read' } },
    component: () => import('@/views/diagnostics/DiagnosticsRequestDetailsPage.vue')
  },
  {
    path: '/preventive-care',
    name: prefix + '.preventive-care',
    meta: { auth: true, name: 'Preventive Care', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/PreventiveCarePage.vue')
  },
  {
    path: '/preventive-care/stations/:stationId/settings',
    name: prefix + '.preventive-care-station-settings',
    meta: { auth: true, name: 'Edit Station Settings', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/StationSettingsPage.vue')
  },
  {
    path: '/preventive-care/stations/:stationId',
    name: prefix + '.preventive-care-station',
    meta: { auth: true, name: 'Preventive Care Station', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/StationPage.vue')
  },
  {
    path: '/preventive-care/stations/:stationId/thresholds',
    name: prefix + '.preventive-care-thresholds',
    meta: { auth: true, name: 'Edit Charger Thresholds', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/ThresholdsPage.vue')
  },
  {
    path: '/preventive-care/stations/:stationId/load-reduction',
    name: prefix + '.preventive-care-load-reduction',
    meta: { auth: true, name: 'Execute Load Reduction', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/LoadReductionPage.vue')
  },
  {
    path: '/preventive-care/technicians',
    name: prefix + '.preventive-care-technicians',
    meta: { auth: true, name: 'Preventive Care Technicians', isBanner: true, requiresSuperAdmin: true },
    beforeEnter: (to, from, next) => {
      const companyId = store.getters['auth/companyID']
      if (companyId === 0) {
        next()
      } else {
        next({ name: prefix + '.dashboard' })
      }
    },
    component: () => import('@/views/preventive-care/TechniciansPage.vue')
  },
  {
    path: '/electricity-tariff-list',
    name: prefix + '.electricity-tariff-list',
    meta: { auth: true, name: 'Electricity Tariff List', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/electricity-tariff/ListPage.vue')
  },
  {
    path: '/electricity-tariff-add',
    name: prefix + '.electricity-tariff-add',
    meta: { auth: true, name: 'Add Electricity Tariff', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/electricity-tariff/AddPage.vue')
  },
  {
    path: '/electricity-tariff-edit/:id',
    name: prefix + '.electricity-tariff-edit',
    meta: { auth: true, name: 'Edit Electricity Tariff', isBanner: true, requiresSuperAdmin: true },
    component: () => import('@/views/electricity-tariff/EditPage.vue')
  },
  {
    path: '/live-sessions',
    name: prefix + '.live-sessions',
    meta: { auth: true, name: 'Live Sessions', isBanner: false, permission: { resource: RESOURCES.LIVE_SESSIONS, action: ACTIONS.READ } },
    component: () => import('@/views/session/LiveSessions.vue')
  },
  {
    path: '/live-monitoring-micro',
    name: prefix + '.live-monitoring-micro',
    meta: { auth: true, name: 'Live Sessions Micro', isBanner: false, permission: { resource: RESOURCES.LIVE_SESSIONS_MICRO, action: ACTIONS.READ } },
    component: () => import('@/views/session/LiveMonitoringPage.vue')
  },
  {
    path: '/live-chargers',
    name: prefix + '.live-chargers',
    meta: { auth: true, name: 'Live Chargers', isBanner: true, permission: { resource: RESOURCES.LIVE_CHARGERS, action: ACTIONS.READ } },
    component: () => import('@/views/charger/RealTimeChargerList.vue')
  },
  // Fleet Management System
  {
    path: '/fleet-dashboard',
    name: prefix + '.fleet-dashboard',
    meta: { auth: true, name: 'Fleet Dashboard', isBanner: false, permission: { resource: RESOURCES.FLEET_DASHBOARD, action: ACTIONS.READ } },
    component: () => import('@/views/dashboards/FleetDashboard.vue')
  },
  {
    path: '/fleet-management/fleets',
    name: prefix + '.fleet-management.fleets.list',
    meta: { auth: true, name: 'Fleet Management', isBanner: true, permission: { resource: RESOURCES.FLEETS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/fleets/ListPage.vue')
  },
  {
    path: '/fleet-management/fleets/add',
    name: prefix + '.fleet-management.fleets.add',
    meta: { auth: true, name: 'Add Fleet', isBanner: true, permission: { resource: RESOURCES.FLEETS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/fleets/AddPage.vue')
  },
  {
    path: '/fleet-management/fleets/edit/:id',
    name: prefix + '.fleet-management.fleets.edit',
    meta: { auth: true, name: 'Edit Fleet', isBanner: true, permission: { resource: RESOURCES.FLEETS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/fleets/EditPage.vue')
  },
  {
    path: '/fleet-management/groups',
    name: prefix + '.fleet-management.groups.list',
    meta: { auth: true, name: 'Group Management', isBanner: true, permission: { resource: RESOURCES.FLEET_GROUPS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/groups/ListPage.vue')
  },
  {
    path: '/fleet-management/groups/add',
    name: prefix + '.fleet-management.groups.add',
    meta: { auth: true, name: 'Add Group', isBanner: true, permission: { resource: RESOURCES.FLEET_GROUPS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/groups/AddPage.vue')
  },
  {
    path: '/fleet-management/groups/edit/:id',
    name: prefix + '.fleet-management.groups.edit',
    meta: { auth: true, name: 'Edit Group', isBanner: true, permission: { resource: RESOURCES.FLEET_GROUPS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/groups/EditPage.vue')
  },
  {
    path: '/fleet-management/vehicles',
    name: prefix + '.fleet-management.vehicles.list',
    meta: { auth: true, name: 'Vehicle Management', isBanner: true, permission: { resource: RESOURCES.FLEET_VEHICLES, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/vehicles/ListPage.vue')
  },
  {
    path: '/fleet-management/vehicles/add',
    name: prefix + '.fleet-management.vehicles.add',
    meta: { auth: true, name: 'Add Vehicle', isBanner: true, permission: { resource: RESOURCES.FLEET_VEHICLES, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/vehicles/AddPage.vue')
  },
  {
    path: '/fleet-management/vehicles/edit/:id',
    name: prefix + '.fleet-management.vehicles.edit',
    meta: { auth: true, name: 'Edit Vehicle', isBanner: true, permission: { resource: RESOURCES.FLEET_VEHICLES, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/vehicles/EditPage.vue')
  },
  {
    path: '/fleet-management/vehicles/:id/sessions',
    name: prefix + '.fleet-management.vehicles.sessions',
    meta: { auth: true, name: 'Vehicle Sessions', isBanner: true, permission: { resource: RESOURCES.FLEET_VEHICLES, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/vehicles/SessionsPage.vue')
  },
  {
    path: '/fleet-management/vehicles/failures',
    name: prefix + '.fleet-management.vehicles.failures',
    meta: { auth: true, name: 'Vehicle Failures', isBanner: true, permission: { resource: RESOURCES.FLEET_VEHICLE_FAILURES, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/vehicles/FailuresPage.vue')
  },
  {
    path: '/fleet-management/accounts',
    name: prefix + '.fleet-management.accounts.list',
    meta: { auth: true, name: 'Fleet Accounts', isBanner: true, permission: { resource: RESOURCES.FLEET_ACCOUNTS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/accounts/ListPage.vue')
  },
  {
    path: '/fleet-management/accounts/unlinked',
    name: prefix + '.fleet-management.accounts.unlinked',
    meta: { auth: true, name: 'Unlinked Fleet Sessions', isBanner: false, permission: { resource: RESOURCES.FLEET_ACCOUNTS_UNLINKED, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/accounts/UnlinkedPage.vue')
  },
  {
    path: '/fleet-management/accounts/add',
    name: prefix + '.fleet-management.accounts.add',
    meta: { auth: true, name: 'Add Fleet Account', isBanner: true, permission: { resource: RESOURCES.FLEET_ACCOUNTS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/accounts/AddPage.vue')
  },
  {
    // Fleet session details, reachable with the fleet session action on fleet
    // accounts instead of the session list permission.
    path: '/fleet-management/sessions/:id',
    name: prefix + '.fleet-management.session-detail',
    meta: { auth: true, name: 'Fleet Session Detail', isBanner: true, fleetSession: true, permission: { resource: RESOURCES.FLEET_ACCOUNTS, action: ACTIONS.FLEET_SESSION } },
    component: () => import('@/views/session/DetailPage.vue')
  },
  {
    path: '/fleet-management/accounts/:id',
    name: prefix + '.fleet-management.accounts.view',
    meta: { auth: true, name: 'Fleet Account Details', isBanner: true, permission: { resource: RESOURCES.FLEET_ACCOUNTS, action: ACTIONS.READ } },
    component: () => import('@/views/fleet-management/accounts/ViewPage.vue')
  },
  {
    path: '/chargers-heartbeats',
    name: prefix + '.chargers-heartbeats',
    meta: { auth: true, name: 'Charger Heartbeat List', isBanner: true, permission: { resource: RESOURCES.CHARGERS_HEARTBEAT, action: ACTIONS.READ } },
    component: () => import('@/views/charger-heartbeat/ListPage.vue')
  },
  {
    path: '/remote-occp-configuration',
    name: prefix + '.remote-occp-configuration',
    meta: { auth: true, name: 'remote-occp-configuration', isBanner: false, permission: { resource: 'maintenance', action: 'read' } },
    component: () => import('@/views/maintenance/RemoteChargerConfiguration.vue')
  },
  {
    path: '/trigger-message',
    name: prefix + '.trigger-message',
    meta: { auth: true, name: 'Trigger Message', isBanner: false, requiresSuperAdmin: true },
    component: () => import('@/views/maintenance/TriggerMessagePage.vue')
  },
]

const errorRoutes = (prefix) => [
  // Error Pages
  {
    path: '403',
    name: prefix + '.403',
    meta: { auth: true, name: 'Error 403', isBanner: true },
    component: () => import('@/views/errors/Error403Page.vue')
  },
  {
    path: '404',
    name: prefix + '.404',
    meta: { auth: false, name: 'Error 404', isBanner: true },
    component: () => import('@/views/errors/Error404Page.vue')
  },
  {
    path: '500',
    name: prefix + '.500',
    meta: { auth: true, name: 'Error 500', isBanner: true },
    component: () => import('@/views/errors/Error500Page.vue')
  },
  {
    path: 'maintenance',
    name: prefix + '.maintenance',
    meta: { auth: true, name: 'Maintenance', isBanner: true },
    component: () => import('@/views/errors/MaintenancePage.vue')
  }
]

const routes = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: defaultChildRoutes('default')
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: authChildRoutes('auth')
  },
  {
    path: '/payment',
    name: 'payment',
    meta: { auth: false, name: 'Payment' },
    component: () => import('@/views/payment/PaymentPage.vue')
  },
  {
    path: '/payment-callback',
    name: 'payment-callback',
    meta: { auth: false, name: 'Payment Callback' },
    component: () => import('@/views/payment/PaymentResultPage.vue')
  },
  {
    path: '/errors',
    name: 'errors',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: errorRoutes('errors')
  },
  // Catch-all route for 404 - redirects to 404 page
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: { name: 'errors.404' }
  }
]

const router = createRouter({
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  if (to.meta.auth && !store.getters['auth/isAuthenticated']) {
    return next({ name: 'auth.login' })
  }

  // The forced change-password screen is only reachable right after a login
  // that was refused because the password expired.
  if (to.meta.requiresPasswordChangeToken && !store.getters['auth/mustChangePassword']) {
    return next({ name: 'auth.login' })
  }

  if (to.meta.auth && store.getters['auth/isAuthenticated'] && !store.getters['auth/isPermissionsLoaded']) {
    await store.dispatch('auth/loadPermissions')
  }

  if (to.meta.requiresSuperAdmin && !store.getters['auth/isSuperAdmin']) {
    return next({ name: 'errors.403' })
  }

  const permission = to.meta.permission;
  if (permission) {
    const hasAccess = store.getters['auth/hasAccess'];
    if (!hasAccess(permission.resource, permission.action)) {
      if (to.name === 'default.dashboard') {
        return next({ name: 'default.empty' });
      }
      return next({ name: 'errors.403' });
    }
  }
  next();
})

export default router
